import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { InputField } from '../components/InputField';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { signIn, loading, user, profile, initialized } = useAuth();
  const navigate = useNavigate();

  // Redirect to profile if already signed in
  React.useEffect(() => {
    // Only redirect if auth is initialized and user is authenticated
    if (initialized && user && profile) {
      // Add a small delay to ensure the auth state is fully settled
      setTimeout(() => {
        navigate('/profile');
      }, 100);
    }
  }, [user, profile, initialized, navigate]);

  const handleLogin = async () => {
    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setError('');

    try {
      // Add a timeout to prevent hanging
      const loginPromise = signIn(email, password);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Login timeout - please try again')), 10000)
      );
      
      await Promise.race([loginPromise, timeoutPromise]);
      
      alert('Login successful!');
      // Add a small delay to ensure the auth state is fully updated
      setTimeout(() => {
        navigate('/profile');
      }, 100);
    } catch (err: any) {
      setError(`Login failed: ${err.message}`);
    }
  };

  const handleForgotPassword = () => {
    alert('Forgot password functionality will be implemented soon!');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <Link to="/" className="back-button">
            <i className="fas fa-arrow-left"></i>
          </Link>
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        <div className="login-form">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="form-section">
            <h3 className="section-title">Account Details</h3>
            <InputField
              label="Email Address *"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email address"
              keyboardType="email-address"
            />
            <InputField
              label="Password *"
              value={password}
              onChangeText={setPassword}
              placeholder="Enter your password"
              secureTextEntry
            />
          </div>

          <div className="forgot-password">
            <button 
              type="button" 
              className="forgot-password-link"
              onClick={handleForgotPassword}
            >
              Forgot your password?
            </button>
          </div>

          <div className="submit-container">
            <Button
              title={loading ? 'Signing In...' : 'Sign In'}
              onPress={handleLogin}
              variant="primary"
              disabled={loading}
            />
          </div>

          <div className="signup-container">
            <span className="signup-text">Don't have an account? </span>
            <Link to="/signup" className="signup-link">
              Create Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
